/**
 * ScriptedGameTransport — the tutorial's implementation of the GameTransport
 * seam (network/gameTransport.ts), generalized over teaching SCENARIOS.
 *
 * It feeds GameScene the SAME payload shapes the production backend sends
 * (captured locally from the real rules/positioning engine — see fixtures/),
 * so the real board rendering, hand, selection, glow, ghost previews,
 * optimistic travel, rollback, pass behavior, round-end and match-end flows
 * all run unchanged. It performs NO networking of any kind:
 *
 *   - never touches ActionCable / getCableConsumer (its channel is local)
 *   - never calls any ApiClient method (state comes from fixtures)
 *   - its session-channel handle is local too: `next_game_created` is how
 *     scenario transitions after a finished round restart the scene through
 *     the SAME production mechanism real rounds use
 *
 * Isolation consequence (verified backend-side): with no API call and no
 * cable subscription, no session/match/statistics/ranking record can exist.
 *
 * state_seq discipline: every served or emitted state is re-stamped from a
 * transport-owned monotonic counter — each scripted advance is accepted by
 * the production admission gate exactly once, and re-fetches (visibility
 * resume, post-restart hydration) read back as harmless duplicates.
 */
import type { GameTransport, GameChannelLike } from '../network/gameTransport';
import type { SessionChannelHandle } from '../network/sessionChannelHub';
import type {
  FullGameStateForUser,
  InitialStateMessage,
  PrivateInitialStateMessage,
  GameUpdateMessage,
  PrivateUpdateMessage,
  GameFinishedMessage,
  ErrorMessage,
  SessionAbandonedMessage,
  RematchStateMessage,
  NextGameCreatedMessage,
} from '../types/game';
import { TUTORIAL_TIMELINE, TUTORIAL_MY_USER_ID, tutorialTileId } from './fixtures/tutorialFixtures';
import { TUTORIAL_SCENARIOS, scenarioTileId, type FinishedFixture } from './fixtures/tutorialScenarios';

/** Pacing between scripted emissions (ms) — mimics natural bot latency. */
const CONFIRM_DELAY_MS = 350;
const BOT_REPLY_DELAY_MS = 950;

/** Structured scripted metadata: every tutorial fixture bot is the Medium
 *  bot (verified against the fixture inventory — 33/33 bot seats). Kept
 *  here, NOT in the fixture files, so the fixtures stay byte-identical. */
const TUTORIAL_BOT_DIFFICULTY = 'medium';

/** Decorate a runtime CLONE of fixture players with the locale-neutral
 *  semantic metadata the shared getPlayerName resolver reads: bots get their
 *  stable difficulty; the local seat is marked 'self' by its fixture user id
 *  (never by matching the English display_name). Fixture objects are never
 *  mutated — callers pass stamped/cloned copies only. */
export function decorateScriptedPlayers(
  players?: Array<{ id?: number; user_id?: number; is_bot?: boolean;
                    bot_difficulty?: string | null; presentation_role?: 'self' | null }>,
): void {
  for (const p of players ?? []) {
    if (p.is_bot) {
      p.bot_difficulty = TUTORIAL_BOT_DIFFICULTY;
    } else if ((p.id ?? p.user_id) === TUTORIAL_MY_USER_ID) {
      p.presentation_role = 'self';
    }
  }
}

export type ScenarioId = 'legal' | 'double' | 'pass' | 'domino' | 'blocked' | 'matchwin';
export const SCENARIO_ORDER: ScenarioId[] = ['legal', 'double', 'pass', 'domino', 'blocked', 'matchwin'];

export type ScriptedTransportEvent =
  | { type: 'action-accepted'; scenario: ScenarioId; action: number }
  | { type: 'action-done'; scenario: ScenarioId; action: number; side?: 'left' | 'right' }
  | { type: 'scenario-loaded'; scenario: ScenarioId }
  | { type: 'wrong-action' }
  /** The dedicated drag lesson: the right tile was played, but with a
   *  click/tap — the step stays active and shows a calm drag reminder. */
  | { type: 'drag-required' };

type Emitter = (evt: ScriptedTransportEvent) => void;

interface ActionScript {
  expect: { type: 'play'; tileId: number } | { type: 'pass' };
  /** The dedicated drag lesson: only a genuine production drag gesture
   *  (Hand's own 'drag' inputMethod, forwarded as meta.viaDrag) completes
   *  this action; a click/tap is calmly rejected with 'drag-required'. */
  requireDrag?: boolean;
  emits?: FullGameStateForUser[];
  emitsBySide?: { left: FullGameStateForUser[]; right: FullGameStateForUser[] };
  finished?: FinishedFixture;
}

interface ScenarioScript {
  id: ScenarioId;
  gameId: number;
  boot: FullGameStateForUser;
  actions: ActionScript[];
  /** Bot-driven ending with no player action (blocked round) — run on demand. */
  auto?: { finished: FinishedFixture };
}

function buildScripts(): ScenarioScript[] {
  const T = TUTORIAL_TIMELINE;
  const S = TUTORIAL_SCENARIOS;
  const soleHandTile = (s: FullGameStateForUser) => s.my_hand[0].id;
  return [
    {
      id: 'legal', gameId: T.boot.id, boot: T.boot,
      actions: [
        { expect: { type: 'play', tileId: tutorialTileId(6, 6) }, emits: [T.afterOpen, ...T.botReplies1] },
        // The dedicated drag-and-drop lesson (single valid destination).
        { expect: { type: 'play', tileId: tutorialTileId(3, 0) }, requireDrag: true, emits: [T.afterMatch, ...T.botReplies2] },
        { expect: { type: 'play', tileId: tutorialTileId(5, 0) }, emitsBySide: { left: [T.finalLeft], right: [T.finalRight] } },
      ],
    },
    {
      id: 'double', gameId: S.double.gameId, boot: S.double.boot,
      actions: [{ expect: { type: 'play', tileId: scenarioTileId('double', 4, 4) }, emits: [S.double.after!] }],
    },
    {
      id: 'pass', gameId: S.pass.gameId, boot: S.pass.boot,
      actions: [{ expect: { type: 'pass' }, emits: [S.pass.after!] }],
    },
    {
      id: 'domino', gameId: S.domino.gameId, boot: S.domino.boot,
      actions: [{ expect: { type: 'play', tileId: soleHandTile(S.domino.boot) }, finished: S.domino.finished! }],
    },
    {
      id: 'blocked', gameId: S.blocked.gameId, boot: S.blocked.boot,
      actions: [],
      auto: { finished: S.blocked.finished! },
    },
    {
      id: 'matchwin', gameId: S.matchwin.gameId, boot: S.matchwin.boot,
      actions: [{ expect: { type: 'play', tileId: soleHandTile(S.matchwin.boot) }, finished: S.matchwin.finished! }],
    },
  ];
}

/** Local, cable-free stand-in for GameChannel. */
class ScriptedGameChannel implements GameChannelLike {
  private _onGameUpdate: ((msg: GameUpdateMessage) => void) | null = null;
  private _onPrivateUpdate: ((msg: PrivateUpdateMessage) => void) | null = null;
  private _onGameFinished: ((msg: GameFinishedMessage) => void) | null = null;
  private _subscribed = false;

  subscribe(): Promise<void> { this._subscribed = true; return Promise.resolve(); }
  unsubscribe(): void {
    this._subscribed = false;
    this._onGameUpdate = null;
    this._onPrivateUpdate = null;
    this._onGameFinished = null;
  }
  get subscribed(): boolean { return this._subscribed; }

  pushGameUpdate(msg: GameUpdateMessage): void { if (this._subscribed) this._onGameUpdate?.(msg); }
  pushPrivateUpdate(msg: PrivateUpdateMessage): void { if (this._subscribed) this._onPrivateUpdate?.(msg); }
  pushGameFinished(msg: GameFinishedMessage): void { if (this._subscribed) this._onGameFinished?.(msg); }

  onInitialState(_cb: (msg: InitialStateMessage) => void): this { return this; }
  onPrivateInitialState(_cb: (msg: PrivateInitialStateMessage) => void): this { return this; }
  onGameUpdate(cb: (msg: GameUpdateMessage) => void): this { this._onGameUpdate = cb; return this; }
  onPrivateUpdate(cb: (msg: PrivateUpdateMessage) => void): this { this._onPrivateUpdate = cb; return this; }
  onGameFinished(cb: (msg: GameFinishedMessage) => void): this { this._onGameFinished = cb; return this; }
  onRematchState(_cb: (msg: RematchStateMessage) => void): this { return this; }
  onSessionAbandoned(_cb: (msg: SessionAbandonedMessage) => void): this { return this; }
  onError(_cb: (msg: ErrorMessage) => void): this { return this; }
  onReconnect(_cb: () => void): this { return this; }
  onDisconnect(_cb: () => void): this { return this; }
  onRejected(_cb: () => void): this { return this; }
}

/** Local session-channel handle: only next_game_created is scripted (the
 *  production round-transition mechanism); everything else is inert. */
class ScriptedSessionHandle {
  private _onNextGameCreated: ((msg: NextGameCreatedMessage) => void) | null = null;
  onNextGameCreated(cb: (msg: NextGameCreatedMessage) => void): this { this._onNextGameCreated = cb; return this; }
  onReconnect(): this { return this; }
  release(): void { this._onNextGameCreated = null; }
  fire(msg: NextGameCreatedMessage): void { this._onNextGameCreated?.(msg); }
}

export class ScriptedGameTransport implements GameTransport {
  private readonly channel = new ScriptedGameChannel();
  private readonly onEvent: Emitter;
  private readonly scripts = buildScripts();
  private scenarioIndex = 0;
  private actionCursor = 0;
  private seq = 0;
  private current: FullGameStateForUser;
  private busy = false;              // scripted emissions in flight
  private roundEnded = false;        // last scenario ended in game_finished
  private timers = new Set<number>();
  private disposed = false;
  private sessionHandles: ScriptedSessionHandle[] = [];

  constructor(opts: { onEvent: Emitter }) {
    this.onEvent = opts.onEvent;
    this.current = this.stamp(this.scripts[0].boot);
  }

  private get scenario(): ScenarioScript { return this.scripts[this.scenarioIndex]; }

  private stamp(fixture: FullGameStateForUser): FullGameStateForUser {
    const copy = JSON.parse(JSON.stringify(fixture)) as FullGameStateForUser;
    copy.state_seq = ++this.seq;
    decorateScriptedPlayers(copy.players);
    return copy;
  }

  // ── GameTransport ────────────────────────────────────────────

  createGameChannel(_gameId?: number, _sceneId?: number): GameChannelLike { return this.channel; }

  getGameState(_gameId?: number): Promise<FullGameStateForUser> {
    return Promise.resolve(JSON.parse(JSON.stringify(this.current)) as FullGameStateForUser);
  }

  playTile(_gameId: number, tileId: number, position: 'left' | 'right', meta?: { viaDrag?: boolean }): Promise<unknown> {
    if (this.disposed) return Promise.reject(new Error('tutorial transport disposed'));
    const action = this.scenario.actions[this.actionCursor];
    if (!this.busy && action && action.expect.type === 'play' && action.expect.tileId === tileId) {
      // The drag lesson accepts ONLY a genuine production drag gesture; a
      // click on the right tile keeps the step active (production rollback
      // returns the tile) and shows the calm reminder — never a punishment.
      if (action.requireDrag && meta?.viaDrag !== true) {
        this.onEvent({ type: 'drag-required' });
        return Promise.reject(new Error('tutorial: this lesson teaches drag-and-drop'));
      }
      this.acceptAction(action, position);
      return Promise.resolve({});
    }
    this.onEvent({ type: 'wrong-action' });
    return Promise.reject(new Error('tutorial: action not part of the current lesson'));
  }

  passTurn(): Promise<unknown> {
    if (this.disposed) return Promise.reject(new Error('tutorial transport disposed'));
    const action = this.scenario.actions[this.actionCursor];
    if (!this.busy && action && action.expect.type === 'pass') {
      this.acceptAction(action, 'left');
      return Promise.resolve({});
    }
    this.onEvent({ type: 'wrong-action' });
    return Promise.reject(new Error('tutorial: passing is not part of this lesson'));
  }

  acquireSessionChannel(): SessionChannelHandle {
    const handle = new ScriptedSessionHandle();
    this.sessionHandles.push(handle);
    return handle as unknown as SessionChannelHandle;
  }

  // ── Scripted flow ────────────────────────────────────────────

  private acceptAction(action: ActionScript, side: 'left' | 'right'): void {
    const scenarioId = this.scenario.id;
    const actionIndex = this.actionCursor;
    this.actionCursor++;
    this.busy = true;
    this.onEvent({ type: 'action-accepted', scenario: scenarioId, action: actionIndex });

    const states = action.emitsBySide ? action.emitsBySide[side] : (action.emits ?? []);
    states.forEach((fixture, i) => {
      this.schedule(CONFIRM_DELAY_MS + i * BOT_REPLY_DELAY_MS, () => {
        this.emitState(fixture);
        if (i === states.length - 1 && !action.finished) {
          this.busy = false;
          this.onEvent({ type: 'action-done', scenario: scenarioId, action: actionIndex, side });
        }
      });
    });
    if (action.finished) {
      const delay = CONFIRM_DELAY_MS + states.length * BOT_REPLY_DELAY_MS;
      this.schedule(delay, () => {
        this.emitFinished(action.finished!);
        this.busy = false;
        this.roundEnded = true;
        this.onEvent({ type: 'action-done', scenario: scenarioId, action: actionIndex, side });
      });
    } else if (states.length === 0) {
      this.busy = false;
      this.onEvent({ type: 'action-done', scenario: scenarioId, action: actionIndex, side });
    }
  }

  /** Blocked scenario: run the bot's round-ending move on demand. */
  runAuto(): void {
    const auto = this.scenario.auto;
    if (!auto || this.busy || this.disposed) return;
    const scenarioId = this.scenario.id;
    this.busy = true;
    this.schedule(BOT_REPLY_DELAY_MS, () => {
      this.emitFinished(auto.finished);
      this.busy = false;
      this.roundEnded = true;
      this.onEvent({ type: 'action-done', scenario: scenarioId, action: -1 });
    });
  }

  /**
   * Load the next teaching scenario. After a finished round this rides the
   * PRODUCTION transition mechanism — a scripted next_game_created restarts
   * GameScene in place (which re-hydrates from getGameState). Mid-game
   * scenarios swap state through an ordinary authoritative update instead.
   */
  advanceScenario(): void {
    if (this.disposed || this.scenarioIndex + 1 >= this.scripts.length) return;
    this.scenarioIndex++;
    this.actionCursor = 0;
    const next = this.scenario;
    this.current = this.stamp(next.boot);
    if (this.roundEnded) {
      this.roundEnded = false;
      const msg = {
        type: 'next_game_created',
        game_id: next.gameId,
        session_id: next.boot.session_id,
        team_scores: next.boot.session_team_scores ?? {},
      } as unknown as NextGameCreatedMessage;
      for (const h of this.sessionHandles.splice(0)) h.fire(msg);
    } else {
      this.emitState(next.boot, /* alreadyStamped */ true);
    }
    this.onEvent({ type: 'scenario-loaded', scenario: next.id });
  }

  private emitState(fixture: FullGameStateForUser, alreadyStamped = false): void {
    const state = alreadyStamped ? this.current : this.stamp(fixture);
    this.current = state;
    this.channel.pushGameUpdate({ type: 'game_update', game_state: state });
    this.channel.pushPrivateUpdate({ type: 'private_update', my_hand: state.my_hand, user_id: TUTORIAL_MY_USER_ID });
  }

  private emitFinished(fin: FinishedFixture): void {
    const state = this.stamp(fin.game_state as FullGameStateForUser);
    this.current = state;
    // Clone before decorating — the fixture's revealed_players stay untouched.
    const revealed = JSON.parse(JSON.stringify(fin.revealed_players)) as typeof fin.revealed_players;
    decorateScriptedPlayers(revealed);
    this.channel.pushGameFinished({
      type: 'game_finished',
      game_state: state,
      team_scores: fin.team_scores,
      session_status: fin.session_status,
      session_winner_team: fin.session_winner_team,
      session_over: fin.session_over,
      target_score: fin.target_score,
      rematch: fin.rematch,
      revealed_players: revealed,
    });
    // Keep the private view coherent (production sends per-user hands with
    // the finish; our captured final state carries my_hand when available).
    if (Array.isArray(state.my_hand)) {
      this.channel.pushPrivateUpdate({ type: 'private_update', my_hand: state.my_hand, user_id: TUTORIAL_MY_USER_ID });
    }
  }

  private schedule(delay: number, fn: () => void): void {
    const id = window.setTimeout(() => {
      this.timers.delete(id);
      if (!this.disposed) fn();
    }, delay);
    this.timers.add(id);
  }

  /** Cancel all pending emissions. Called on screen unmount. */
  dispose(): void {
    this.disposed = true;
    for (const id of this.timers) window.clearTimeout(id);
    this.timers.clear();
    this.channel.unsubscribe();
    this.sessionHandles = [];
  }
}
